import { Injectable } from '@angular/core';
import { Group, ApplicationSettings } from '@icure/api';
import { GroupService } from './group.service';
import { ApplicationSettingsService } from './application-settings.service';
import { getAppSetting, getAppSettingContent } from '../utils/appSetting-utils';
import { BatchOperationEnum } from '../models/batch.model';
import {
  AccessMatrixService,
  ModuleTypeEnum,
  RouteTypeEnum,
  SubModuleTypeEnum,
} from './access-matrix.service';

/**
 * Callback invoked for every result line produced by an operation, so the
 * caller can display results incrementally.
 */
export type BatchResultLineHandler = (line: string) => void;

const APPLICATION_SETTINGS_JAVA_TYPE =
  'org.taktik.icure.entities.ApplicationSettings';

@Injectable({
  providedIn: 'root',
})
export class BatchService {
  constructor(
    private groupService: GroupService,
    private applicationSettingsService: ApplicationSettingsService,
    private accessMatrixService: AccessMatrixService,
  ) {}

  /**
   * Execute the given batch operation against the provided group IDs,
   * emitting one result line per processed group through `onLine`.
   *
   * When `dryRun` is true, operations must not persist any change.
   */
  public async execute(
    operation: BatchOperationEnum,
    groupIds: string[],
    parameters: Record<string, unknown>,
    dryRun: boolean,
    onLine: BatchResultLineHandler,
  ): Promise<void> {
    if (
      !dryRun &&
      !this.accessMatrixService.isAvailableModule(
        RouteTypeEnum.BATCH,
        ModuleTypeEnum.BATCH_OPERATION,
        SubModuleTypeEnum.BATCH_OPERATION_WRITE,
      )
    ) {
      throw new Error(
        'Insufficient rights to run a batch with dry-run disabled',
      );
    }

    switch (operation) {
      case BatchOperationEnum.LIST_APP_SETTINGS_VALUES:
        await this.listAppSettingsValues(groupIds, parameters, onLine);
        break;
      case BatchOperationEnum.UPDATE_APP_SETTINGS_VALUE:
        await this.updateAppSettingsValue(groupIds, parameters, dryRun, onLine);
        break;
    }
  }

  private async listAppSettingsValues(
    groupIds: string[],
    parameters: Record<string, unknown>,
    onLine: BatchResultLineHandler,
  ): Promise<void> {
    const appSettings: string = String(parameters['appSettings'] ?? '').trim();
    const key: string = String(parameters['key'] ?? '').trim();

    if (!appSettings || !key) {
      throw new Error('Missing required parameters: appSettings, key');
    }

    for (const groupId of this.normalizeGroupIds(groupIds)) {
      const line: string = await this.readAppSettingLine(
        groupId,
        appSettings,
        key,
      );
      onLine(line);
    }
  }

  private async readAppSettingLine(
    groupId: string,
    appSettings: string,
    key: string,
  ): Promise<string> {
    try {
      const group: Group = await this.groupService.getGroup(groupId);
      const cluster: string = this.extractCluster(group);
      const allSettings: ApplicationSettings[] =
        await this.applicationSettingsService.getAllApplicationSettings(
          groupId,
          group?.password ?? '',
          cluster,
        );
      const setting: ApplicationSettings | undefined = getAppSetting(
        allSettings,
        [appSettings],
      )?.[0];
      const value: string | undefined = setting
        ? getAppSettingContent(setting)?.[key]
        : undefined;
      return `${groupId} : ${appSettings} - ${key} = ${value}`;
    } catch (error) {
      return `${groupId} : ${appSettings} - ${key} = <erreur: ${(error as Error)?.message ?? error}>`;
    }
  }

  private async updateAppSettingsValue(
    groupIds: string[],
    parameters: Record<string, unknown>,
    dryRun: boolean,
    onLine: BatchResultLineHandler,
  ): Promise<void> {
    const appSettings: string = String(
      parameters?.['appSettings'] ?? '',
    ).trim();
    const key: string = String(parameters?.['key'] ?? '').trim();
    const value: string = String(parameters?.['value'] ?? '').trim();

    if (!appSettings || !key || !value) {
      throw new Error('Missing required parameters: appSettings, key, value');
    }

    for (const groupId of this.normalizeGroupIds(groupIds)) {
      const line: string = await this.updateAppSettingLine(
        groupId,
        appSettings,
        key,
        value,
        dryRun,
      );
      onLine(line);
    }
  }

  private async updateAppSettingLine(
    groupId: string,
    appSettings: string,
    key: string,
    value: string,
    dryRun: boolean,
  ): Promise<string> {
    try {
      const group: Group = await this.groupService.getGroup(groupId);
      const cluster: string = this.extractCluster(group);
      const groupPassword: string = group?.password ?? '';
      const allSettings: ApplicationSettings[] =
        await this.applicationSettingsService.getAllApplicationSettings(
          groupId,
          groupPassword,
          cluster,
        );
      const setting: ApplicationSettings | undefined = getAppSetting(
        allSettings,
        [appSettings],
      )?.[0];
      const currentSettings: { [settingKey: string]: string } =
        (setting ? getAppSettingContent(setting) : {}) ?? {};
      const existingValue: string | undefined = currentSettings[key];

      if (!dryRun) {
        const payload = {
          _id: appSettings,
          settings: { ...currentSettings, [key]: value },
          java_type: APPLICATION_SETTINGS_JAVA_TYPE,
          rev_history: {},
        };
        await this.applicationSettingsService.createApplicationSettings(
          groupId,
          groupPassword,
          cluster,
          payload,
        );
      }

      return `${groupId} : ${appSettings} - ${key} : ${existingValue} -> ${value}`;
    } catch (error) {
      return `${groupId} : ${appSettings} - ${key} = <erreur: ${(error as Error)?.message ?? error}>`;
    }
  }

  private extractCluster(group: Group): string {
    return (
      group?.servers?.[0]
        ?.replace('https://', '')
        ?.replace('.icure.cloud', '')
        ?.replace(':444', '') ?? ''
    );
  }

  /**
   * Defensively trims each group ID and drops empty entries, so callers do
   * not have to pre-clean the list and stray whitespace never reaches the API.
   */
  private normalizeGroupIds(groupIds: string[]): string[] {
    return (groupIds ?? [])
      .map((groupId: string): string => groupId?.trim() ?? '')
      .filter((groupId: string): boolean => groupId.length > 0);
  }
}
